import { Box, Typography, TextField, MenuItem, useTheme } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useEffect, useState } from "react";
import { requester } from "api/pesabackend";

export default function InvoiceDetailForm({
  index,
  data,
  handleRemoveProductDetailRow,
  handleItemAmountChange,
  handleItemDiscountChange,
  handleChange,
  errors,
  touched,
  name,
  handleBlur,
  setFieldValue,
}) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const bodyBg  = theme.palette.background.alt;

  const [unitList,      setUnitList]      = useState([]);
  const [batchList,     setBatchList]     = useState([]);
  const [selectedUnit,  setSelectedUnit]  = useState(data.related_product_unit || "");
  const [selectedBatch, setSelectedBatch] = useState(data.batch || "");
  const [quantity,      setQuantity]      = useState(data.quantity || 1);
  const [salePrice,     setSalePrice]     = useState(data.sale_price || 0);
  const [discount,      setDiscount]      = useState(data.discount || 0);
  const [total,         setTotal]         = useState(data.total || 0);

  useEffect(() => {
    if (data.related_product) {
      fetchUnits(data.related_product);
      fetchBatches(data.related_product);
    }
  }, [data.related_product]);

  const fetchUnits = async (productId) => {
    try {
      const res = await requester.get(`/inventory/products/unit_list/${productId}`);
      setUnitList(res.data.data ?? []);
    } catch {}
  };

  const fetchBatches = async (productId) => {
    try {
      const res = await requester.get(`/inventory/products/batch_list/${productId}`);
      setBatchList(res.data.data ?? []);
    } catch {}
  };

  const recalcTotal = (qty, price, disc) => {
    const t = ((parseFloat(qty) * parseFloat(price)) - parseFloat(disc || 0)).toFixed(2);
    return isNaN(Number(t)) ? 0 : Math.max(0, Number(t));
  };

  const handleUnitChange = (e) => {
    const unitId = e.target.value;
    setSelectedUnit(unitId);
    setFieldValue(`${name}.related_product_unit`, unitId);
    const unit  = unitList.find((u) => u.id === unitId);
    const price = unit?.unit_selling_price ?? salePrice;
    setSalePrice(price);
    setFieldValue(`${name}.sale_price`, price);
    const newTotal = recalcTotal(quantity, price, discount);
    setTotal(newTotal);
    handleItemAmountChange(price, newTotal, quantity, selectedBatch, data.product_name, unit?.related_business_product_unit?.name ?? "", data.related_product, unitId, index);
  };

  const handleBatchChange = (e) => {
    const batchNo = e.target.value;
    setSelectedBatch(batchNo);
    setFieldValue(`${name}.batch`, batchNo);
    handleItemAmountChange(salePrice, total, quantity, batchNo, data.product_name, data.product_unit, data.related_product, selectedUnit, index);
  };

  const handleQtyChange = (e) => {
    const qty = e.target.value;
    setQuantity(qty);
    setFieldValue(`${name}.quantity`, qty);
    const newTotal = recalcTotal(qty, salePrice, discount);
    setTotal(newTotal);
    handleItemAmountChange(salePrice, newTotal, qty, selectedBatch, data.product_name, data.product_unit, data.related_product, selectedUnit, index);
  };

  /* ── NEW: price is now editable ── */
  const handlePriceChange = (e) => {
    const price = e.target.value;
    setSalePrice(price);
    setFieldValue(`${name}.sale_price`, price);
    const newTotal = recalcTotal(quantity, price, discount);
    setTotal(newTotal);
    handleItemAmountChange(price, newTotal, quantity, selectedBatch, data.product_name, data.product_unit, data.related_product, selectedUnit, index);
  };

  const handleDiscountChange = (e) => {
    const disc = e.target.value;
    setDiscount(disc);
    setFieldValue(`${name}.discount`, disc);
    const newTotal = recalcTotal(quantity, salePrice, disc);
    setTotal(newTotal);
    handleItemDiscountChange(e, index);
  };

  const fieldSx = {
    "& .MuiInputBase-root": {
      fontSize: "13px", height: "30px",
      backgroundColor: bodyBg, borderRadius: "7px",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "11px" },
    "& .MuiSelect-select": { fontSize: "13px" },
  };

  /* ── Editable price field with a subtle visual cue that it's overridable ── */
  const editedPriceSx = {
    ...fieldSx,
    "& .MuiInputBase-root": {
      ...fieldSx["& .MuiInputBase-root"],
      backgroundColor: parseFloat(salePrice) !== parseFloat(data.sale_price || 0)
        ? (isDark ? "rgba(186,117,23,0.08)" : "rgba(186,117,23,0.06)")
        : bodyBg,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: parseFloat(salePrice) !== parseFloat(data.sale_price || 0)
        ? "rgba(186,117,23,0.5)"
        : border,
    },
  };

  const errField = (field) => {
    const e = errors?.invoice_products?.[index]?.[field];
    const t = touched?.invoice_products?.[index]?.[field];
    return Boolean(t && e);
  };

  return (
    <Box sx={{
      backgroundColor: cardBg,
      border: `0.5px solid ${border}`,
      borderRadius: "10px",
    }}>
      {/* ── Card header: product name + delete ── */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: "8px",
        px: "14px", py: "5px",
        borderBottom: `0.5px solid ${border}`,
        backgroundColor: isDark ? "rgba(255,255,255,0.015)" : "#f9faf9",
      }}>
        <Box sx={{
          fontSize: "10px", fontWeight: 600, letterSpacing: "0.06em",
          color: textSec, backgroundColor: bodyBg,
          border: `0.5px solid ${border}`, borderRadius: "4px",
          px: "6px", py: "2px", flexShrink: 0,
        }}>
          #{index + 1}
        </Box>

        <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri, flex: 1 }}>
          {data.product_name || "—"}
        </Typography>

        <Box
          onClick={() => handleRemoveProductDetailRow(index)}
          sx={{
            width: "26px", height: "26px", borderRadius: "6px",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", color: "#e24b4a",
            border: "0.5px solid rgba(226,75,74,0.25)",
            "&:hover": { backgroundColor: "rgba(226,75,74,0.07)" },
            transition: "all 0.15s", flexShrink: 0,
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: "14px" }} />
        </Box>
      </Box>

      {/* ── Field row ── */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 80px 150px 100px 90px",
        gap: "8px",
        px: "14px", py: "10px",
        alignItems: "center",
      }}>
        {/* Unit */}
        <Box>
          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: textSec, mb: "4px" }}>
            Unit
          </Typography>
          <TextField
            select size="small" fullWidth
            value={selectedUnit}
            onChange={handleUnitChange}
            onBlur={handleBlur}
            name={`${name}.related_product_unit`}
            error={errField("related_product_unit")}
            sx={fieldSx}
          >
            {unitList.length === 0
              ? <MenuItem value="" disabled>No units</MenuItem>
              : unitList.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.related_business_product_unit?.name}
                </MenuItem>
              ))
            }
          </TextField>
        </Box>

        {/* Batch */}
        <Box>
          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: textSec, mb: "4px" }}>
            Batch
          </Typography>
          <TextField
            select size="small" fullWidth
            value={selectedBatch}
            onChange={handleBatchChange}
            name={`${name}.batch`}
            onBlur={handleBlur}
            error={errField("batch")}
            sx={fieldSx}
          >
            {batchList.length === 0
              ? <MenuItem value="" disabled>No batches</MenuItem>
              : batchList.map((b) => (
                <MenuItem key={b.id} value={b.batch}>
                  {b.batch} - {b.available_stock} available
                </MenuItem>
              ))
            }
          </TextField>
        </Box>

        {/* Qty */}
        <Box>
          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: textSec, mb: "4px" }}>
            Qty
          </Typography>
          <TextField
            size="small" fullWidth type="number"
            value={quantity}
            onChange={handleQtyChange}
            onBlur={handleBlur}
            name={`${name}.quantity`}
            error={errField("quantity")}
            inputProps={{ min: 0.5, step: 0.5 }}
            sx={fieldSx}
          />
        </Box>

        {/* Price — now editable, turns amber when overridden */}
        <Box>
          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: textSec, mb: "4px" }}>
            Price{" "}
            {parseFloat(salePrice) !== parseFloat(data.sale_price || 0) && (
              <Box
                component="span"
                onClick={() => {
                  const original = data.sale_price || 0;
                  setSalePrice(original);
                  setFieldValue(`${name}.sale_price`, original);
                  const newTotal = recalcTotal(quantity, original, discount);
                  setTotal(newTotal);
                  handleItemAmountChange(original, newTotal, quantity, selectedBatch, data.product_name, data.product_unit, data.related_product, selectedUnit, index);
                }}
                sx={{ color: "#BA7517", cursor: "pointer", ml: "4px", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}
              >
                ↩ reset
              </Box>
            )}
          </Typography>
          <TextField
            size="small" fullWidth type="number"
            value={salePrice}
            onChange={handlePriceChange}
            onBlur={handleBlur}
            name={`${name}.sale_price`}
            inputProps={{ min: 0, step: 0.01 }}
            sx={editedPriceSx}
          />
        </Box>

        {/* Discount */}
        <Box>
          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: textSec, mb: "4px" }}>
            Discount
          </Typography>
          <TextField
            size="small" fullWidth type="number"
            value={discount}
            onChange={handleDiscountChange}
            name={`${name}.discount`}
            onBlur={handleBlur}
            inputProps={{ min: 0, step: 1 }}
            sx={fieldSx}
          />
        </Box>

        {/* Total */}
        <Box>
          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: accent, mb: "4px" }}>
            Total
          </Typography>
          <Box sx={{
            height: "34px", px: "10px",
            display: "flex", alignItems: "center",
            fontSize: "14px", fontWeight: 500, color: accent,
          }}>
            {Number(total).toFixed(2)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}